// ============================================================================
// S3 BROWSER HOOK
// React Query hooks for S3 bucket browsing
// ============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';

// ============================================================================
// TYPES
// ============================================================================

export interface S3Folder {
  type: 'folder';
  name: string;
  path: string;
  fullPath: string;
}

export interface S3File {
  type: 'file';
  name: string;
  path: string;
  fullPath: string;
  size: number;
  lastModified: string;
  etag: string;
}

export interface S3BrowseResponse {
  success: boolean;
  prefix: string;
  folders: S3Folder[];
  files: S3File[];
  isTruncated: boolean;
}

export interface S3DownloadResponse {
  success: boolean;
  downloadUrl: string;
  expiresAt: string;
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

const s3Api = {
  browseS3: async (
    projectId: string,
    prefix: string | undefined
  ): Promise<S3BrowseResponse> => {
    const params = prefix ? { prefix } : {};
    const response = await api.get(
      `/api/projects/${projectId}/s3/browse`,
      { params }
    );
    return response.data;
  },

  getDownloadUrl: async (
    projectId: string,
    key: string
  ): Promise<S3DownloadResponse> => {
    const response = await api.get(
      `/api/projects/${projectId}/s3/download`,
      { params: { key } }
    );
    return response.data;
  },

  deleteObjects: async (
    projectId: string,
    keys: string[]
  ): Promise<{ success: boolean; deletedCount: number; requestedCount: number }> => {
    const response = await api.delete(
      `/api/projects/${projectId}/s3/delete`,
      { data: { keys } }
    );
    return response.data;
  },

  uploadFile: async (
    projectId: string,
    file: File,
    prefix: string
  ): Promise<{ success: boolean; key: string; size: number; contentType: string; message: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('prefix', prefix);

    const response = await api.post(
      `/api/projects/${projectId}/s3/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      }
    );
    return response.data;
  }
};

// ============================================================================
// HOOKS
// ============================================================================

/**
 * Hook to browse S3 bucket contents
 */
export function useS3Browse(projectId: string, token: string, prefix?: string) {
  return useQuery({
    queryKey: ['s3-browse', projectId, prefix],
    queryFn: () => s3Api.browseS3(projectId, prefix),
    enabled: !!projectId && !!token,
    staleTime: 30000, // 30 seconds
    retry: 2
  });
}

/**
 * Function to get download URL for S3 object
 * Use this directly when user clicks download
 */
export async function downloadS3File(
  projectId: string,
  key: string,
  _token: string
): Promise<string> {
  const result = await s3Api.getDownloadUrl(projectId, key);
  return result.downloadUrl;
}

/**
 * Hook to delete S3 objects
 */
export function useDeleteS3Objects(projectId: string, token: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (keys: string[]) => s3Api.deleteObjects(projectId, keys),
    onSuccess: () => {
      // Invalidate all S3 browse queries for this project
      queryClient.invalidateQueries({ queryKey: ['s3-browse', projectId] });
    }
  });
}

/**
 * Hook to upload file to S3
 */
export function useUploadS3File(projectId: string, token: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ file, prefix }: { file: File; prefix: string }) =>
      s3Api.uploadFile(projectId, file, prefix),
    onSuccess: () => {
      // Invalidate all S3 browse queries for this project
      queryClient.invalidateQueries({ queryKey: ['s3-browse', projectId] });
    }
  });
}
